// screens/Personal.tsx — the cockpit's Personal tab (build 1 of the 01B nav,
// 2026-10-08; spec `spec-the-cockpit-personal-tab-and-the-01b-nav-build-1-
// 2026-10-08` §2b/§2c, plates 01B and 02).
//
// A sub-tab strip — Calendar · To-dos · Lists · People · Notes — over the
// sub-tab's own screen. The sub-tab is IN THE URL (`#/personal/<sub>`), so
// back/forward and a reload keep the place. Calendar, To-dos and People are
// the EXISTING screens, mounted as they are (no fork of their logic); Lists
// and Notes are this tab's own.
//
// ⚑ THE FAMILY TENANT IS READ BESIDE, NEVER SWITCHED TO (ruling N3). The
// server answers `/api/personal/family` with the tenant named by the lever
// `personal.family_tenant_id` ONLY when the viewer is an active member of it;
// otherwise null, and nothing family-shaped renders — no toggle, no error card.
//
// ⚑ WHERE A WRITE GOES. When a family tenant resolves, a new event, a
// standalone reminder and a new list are written THERE, with the private/
// family choice as their visibility. That is deliberate and it is about
// delivery, not tidiness: the reminder worker that actually sends a reminder
// (Coconup's router, spec-coconup-calendar-reminders-lists §5b) claims from
// the family tenant's service key and no other. A reminder written into the
// cockpit's own tenant would sit `pending` with nothing to send it — the
// silent failure this product must never have. With no family tenant, writes
// land in the cockpit's own tenant.
import { useCallback, useEffect, useState } from 'preact/hooks';
import {
  cancelAuthoredEvent,
  cancelReminder,
  createAuthoredEvent,
  createReminder,
  getGoogleConnections,
  getPersonalFamily,
  getTeamMe,
  listPersonalMembers,
  listReminders,
  updateAuthoredEvent,
  type CalendarEventApi,
  type FamilyTenantApi,
  type FamilyVisibility,
  type ReminderApi,
} from '../lib/api';
import { PERSONAL_SUBS, routePath, type PersonalSub } from '../routes/route';
import { Calendar } from './Calendar';
import { Todos } from './Todos';
import { People } from './People';
import { Lists } from './Lists';
import { PersonalNotes } from './PersonalNotes';
import { ConnectedAccounts } from './ConnectedAccounts';
import {
  PersonalSheet,
  failureOf,
  localDate,
  localZone,
  nextHour,
  wallClockLabel,
} from './personalShared';
import './Personal.css';

export interface PersonalProps {
  tenantId: string;
  sub: PersonalSub;
  /** The To-dos filter (the Todos screen's own `state`). */
  state?: string;
  /** The list open on the Lists sub-tab. */
  listId?: string;
  navigate: (path: string) => void;
  onOpenEvent: (id: string) => void;
  onOpenThread: (id: string) => void;
  onOpenPerson: (id: string) => void;
}

/** What the tab knows about who is looking and who else is here. */
export interface PersonalContext {
  family: FamilyTenantApi | null;
  meId: string | null;
  /** userId -> name, across the cockpit's tenant and the family's. */
  names: Record<string, string>;
  /** False until the family question is answered. The calendar and the lists
   * wait for it, so they read both tenants ONCE — never the cockpit's alone
   * and then again with the family, which painted Mine/Family toggles over
   * an agenda that did not yet hold the family (seen on the 390px walk). */
  ready: boolean;
}

function usePersonalContext(tenantId: string): PersonalContext {
  const [ctx, setCtx] = useState<PersonalContext>({ family: null, meId: null, names: {}, ready: false });
  useEffect(() => {
    let cancelled = false;
    // Each read is best-effort ON ITS OWN: a failed family read means no
    // Family toggle (never an error card — N3), a failed name read means a
    // row says less, and neither stops the screen.
    const family = getPersonalFamily(tenantId).catch(() => {
      // N3: no family answer is "no Family toggle", never an error card.
      return null;
    });
    const me = getTeamMe(tenantId).then((r) => r.member.userId, () => null);
    void Promise.all([family, me]).then(async ([fam, meId]) => {
      const tenants = [tenantId, ...(fam ? [fam.tenantId] : [])];
      const lists = await Promise.all(
        tenants.map((t) =>
          listPersonalMembers(t).catch(() => {
            // A side read: without names a row says less ("added by" drops),
            // and every list/event read still reports its own failure loudly.
            return [];
          })
        )
      );
      const names: Record<string, string> = {};
      for (const m of lists.flat()) if (m.name) names[m.userId] = m.name;
      if (!cancelled) setCtx({ family: fam, meId, names, ready: true });
    });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);
  return ctx;
}

/** G1 — whether this tenant offers Google connections at all. A failed read
 * means no Accounts tab, never an error card (the same posture as N3). */
function useGoogleConnectionsOn(tenantId: string): boolean {
  const [on, setOn] = useState(false);
  useEffect(() => {
    let cancelled = false;
    getGoogleConnections(tenantId).then(
      (r) => { if (!cancelled) setOn(r.configured && r.enabled); },
      () => { if (!cancelled) setOn(false); }
    );
    return () => { cancelled = true; };
  }, [tenantId]);
  return on;
}

export function Personal(props: PersonalProps) {
  const { tenantId, sub, navigate } = props;
  const ctx = usePersonalContext(tenantId);
  const googleOn = useGoogleConnectionsOn(tenantId);
  // Accounts shows where Google connections are on — and always while you are
  // on it, so a link to it never lands on a strip that hides where you are.
  const subs = PERSONAL_SUBS.filter((s) => s.id !== 'accounts' || googleOn || sub === 'accounts');

  let pane;
  switch (sub) {
    case 'calendar':
      pane = ctx.ready ? (
        <PersonalCalendar tenantId={tenantId} ctx={ctx} onOpenEvent={props.onOpenEvent} />
      ) : (
        <div class="personal__waiting" aria-hidden="true" />
      );
      break;
    case 'todos':
      pane = <Todos tenantId={tenantId} state={props.state} onOpenThread={props.onOpenThread} />;
      break;
    case 'lists':
      pane = !ctx.ready ? (
        <div class="personal__waiting" aria-hidden="true" />
      ) : (
        <Lists
          tenantId={tenantId}
          ctx={ctx}
          listId={props.listId}
          onSelectList={(listId) => navigate(routePath({ screen: 'personal', sub: 'lists', listId }))}
        />
      );
      break;
    case 'people':
      pane = <People tenantId={tenantId} onOpen={props.onOpenPerson} />;
      break;
    case 'notes':
      pane = <PersonalNotes tenantId={tenantId} />;
      break;
    case 'accounts':
      pane = <ConnectedAccounts tenantId={tenantId} outcome={props.state} />;
      break;
  }

  return (
    <div class="personal">
      <nav class="personal__tabs" aria-label="Personal">
        {subs.map((s) => (
          <a
            key={s.id}
            class={`personal__tab${s.id === sub ? ' is-active' : ''}`}
            href={`#${routePath({ screen: 'personal', sub: s.id })}`}
            aria-current={s.id === sub ? 'page' : undefined}
            onClick={(e) => {
              e.preventDefault();
              navigate(routePath({ screen: 'personal', sub: s.id }));
            }}
          >
            {s.label}
          </a>
        ))}
      </nav>
      <div class="personal__pane">{pane}</div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Calendar — the existing agenda, plus Mine/Family, New event, Remind me.
 * ------------------------------------------------------------------ */

type RepeatChoice = 'none' | 'DAILY' | 'WEEKLY' | 'MONTHLY';
type RemindChoice = 'none' | '0' | '10' | '60' | '1440';

const REPEAT_OPTIONS: Array<{ id: RepeatChoice; label: string }> = [
  { id: 'none', label: 'Does not repeat' },
  { id: 'DAILY', label: 'Every day' },
  { id: 'WEEKLY', label: 'Every week' },
  { id: 'MONTHLY', label: 'Every month' },
];

const REMIND_OPTIONS: Array<{ id: RemindChoice; label: string }> = [
  { id: 'none', label: 'No reminder' },
  { id: '0', label: 'At the time' },
  { id: '10', label: '10 minutes before' },
  { id: '60', label: '1 hour before' },
  { id: '1440', label: '1 day before' },
];

const REMIND_PHRASE: Record<RemindChoice, string> = {
  none: '',
  '0': ', reminds at the time',
  '10': ', reminds 10 minutes before',
  '60': ', reminds 1 hour before',
  '1440': ', reminds 1 day before',
};

type Sheet =
  | null
  | { kind: 'event'; editing: { event: CalendarEventApi; tenantId: string } | null }
  | { kind: 'reminder' };

function PersonalCalendar({
  tenantId,
  ctx,
  onOpenEvent,
}: {
  tenantId: string;
  ctx: PersonalContext;
  onOpenEvent: (id: string) => void;
}) {
  const [showMine, setShowMine] = useState(true);
  const [showFamily, setShowFamily] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [sheet, setSheet] = useState<Sheet>(null);
  const [receipt, setReceipt] = useState<string | null>(null);
  const closeSheet = useCallback(() => setSheet(null), []);
  const writeTenant = ctx.family?.tenantId ?? tenantId;

  function done(line: string) {
    setSheet(null);
    setReceipt(line);
    setReloadKey((k) => k + 1);
  }

  // "Thursday 8" — plate 01B's order, built from its parts: a locale's own
  // weekday+day pattern can say "8 Thursday".
  const now = new Date();
  const today = `${now.toLocaleDateString([], { weekday: 'long' })} ${now.getDate()}`;
  const header = (
    <div class="pcal__head">
      <div class="pcal__headline">
        <h1 class="pcal__day">{today}</h1>
        {ctx.family ? (
          <div class="pcal__whose" role="group" aria-label="Whose calendar">
            <button
              type="button"
              class={`pcal__toggle is-mine${showMine ? ' is-on' : ''}`}
              aria-pressed={showMine}
              onClick={() => setShowMine((v) => !v)}
            >
              Mine
            </button>
            <button
              type="button"
              class={`pcal__toggle is-family${showFamily ? ' is-on' : ''}`}
              aria-pressed={showFamily}
              onClick={() => setShowFamily((v) => !v)}
            >
              Family
            </button>
          </div>
        ) : null}
      </div>
      {receipt ? (
        <div class="personal__receipt" role="status">
          {receipt}
        </div>
      ) : null}
    </div>
  );

  return (
    <div class="pcal">
      <Calendar
        tenantId={tenantId}
        onOpen={onOpenEvent}
        embedded
        family={ctx.family}
        showMine={showMine}
        showFamily={showFamily}
        reloadKey={reloadKey}
        names={ctx.names}
        meId={ctx.meId}
        onEditAuthored={(event, eventTenant) => setSheet({ kind: 'event', editing: { event, tenantId: eventTenant } })}
        header={header}
        footer={
          <PersonalReminders
            tenants={[tenantId, ...(ctx.family ? [ctx.family.tenantId] : [])]}
            reloadKey={reloadKey}
            onCancelled={(text) => done(`Cancelled the reminder “${text}”.`)}
          />
        }
      />
      <div class="personal__actions">
        <button type="button" class="personal__primary" onClick={() => setSheet({ kind: 'event', editing: null })}>
          New event
        </button>
        <button type="button" class="personal__secondary" onClick={() => setSheet({ kind: 'reminder' })}>
          Remind me
        </button>
      </div>
      {sheet?.kind === 'event' ? (
        <EventSheet
          writeTenant={writeTenant}
          hasFamily={ctx.family !== null}
          editing={sheet.editing}
          onClose={closeSheet}
          onDone={done}
        />
      ) : null}
      {sheet?.kind === 'reminder' ? (
        <ReminderSheet writeTenant={writeTenant} onClose={closeSheet} onDone={done} />
      ) : null}
    </div>
  );
}

function remindChoiceOf(offsets: number[] | undefined): RemindChoice {
  const first = offsets?.[0];
  if (first === undefined) return 'none';
  const id = String(first) as RemindChoice;
  return REMIND_OPTIONS.some((o) => o.id === id) ? id : 'none';
}

function repeatChoiceOf(rrule: string | null | undefined): RepeatChoice {
  const freq = rrule ? /FREQ=([A-Z]+)/.exec(rrule)?.[1] : undefined;
  return freq === 'DAILY' || freq === 'WEEKLY' || freq === 'MONTHLY' ? freq : 'none';
}

function EventSheet({
  writeTenant,
  hasFamily,
  editing,
  onClose,
  onDone,
}: {
  writeTenant: string;
  hasFamily: boolean;
  editing: { event: CalendarEventApi; tenantId: string } | null;
  onClose: () => void;
  onDone: (receipt: string) => void;
}) {
  const ev = editing?.event ?? null;
  const startLocal = ev?.startLocal ?? null;
  const [title, setTitle] = useState(ev?.title ?? '');
  const [date, setDate] = useState(startLocal ? startLocal.slice(0, 10) : localDate(new Date()));
  const [time, setTime] = useState(startLocal && startLocal.length > 10 ? startLocal.slice(11, 16) : nextHour());
  const [allDay, setAllDay] = useState(ev?.allDay === true);
  const [repeat, setRepeat] = useState<RepeatChoice>(repeatChoiceOf(ev?.recurrenceRule));
  const [visibility, setVisibility] = useState<FamilyVisibility>(ev?.visibility === 'private' ? 'private' : 'family');
  const [remind, setRemind] = useState<RemindChoice>(remindChoiceOf(ev?.remindMinutesBefore));
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);

  async function save(e: Event) {
    e.preventDefault();
    if (busy) return;
    const name = title.trim();
    if (!name) {
      setNote('An event needs a title.');
      return;
    }
    setBusy(true);
    setNote(null);
    const fields = {
      title: name,
      start: allDay ? date : `${date}T${time}`,
      allDay,
      timezone: ev?.timezone ?? localZone(),
      rrule: repeat === 'none' ? null : `FREQ=${repeat}`,
      visibility,
      remindMinutesBefore: remind === 'none' ? [] : [Number(remind)],
    };
    try {
      const saved = editing
        ? await updateAuthoredEvent(editing.tenantId, editing.event.id, fields)
        : await createAuthoredEvent(writeTenant, fields);
      const when = wallClockLabel(saved.startLocal, saved.allDay);
      const whose = saved.visibility === 'private' ? 'only you' : hasFamily ? 'family' : 'shared';
      onDone(`${editing ? 'Changed' : 'Added'} “${saved.title ?? name}” — ${when}, ${whose}${REMIND_PHRASE[remind]}.`);
    } catch (err) {
      setNote(`Not ${editing ? 'changed' : 'added'} — ${failureOf(err)}`);
      setBusy(false);
    }
  }

  async function cancelEvent() {
    if (!editing || busy) return;
    setBusy(true);
    try {
      const cancelled = await cancelAuthoredEvent(editing.tenantId, editing.event.id);
      onDone(`Cancelled “${cancelled.title ?? editing.event.title ?? 'the event'}”. It stays on the calendar, struck.`);
    } catch (err) {
      setNote(`Not cancelled — ${failureOf(err)}`);
      setBusy(false);
    }
  }

  return (
    <PersonalSheet title={editing ? 'Change event' : 'New event'} onClose={onClose}>
      <form class="psheet__form" onSubmit={save}>
        <label class="psheet__field">
          <span>Title</span>
          <input type="text" value={title} maxLength={200} onInput={(e) => setTitle(e.currentTarget.value)} />
        </label>
        <div class="psheet__row">
          <label class="psheet__field">
            <span>Date</span>
            <input type="date" value={date} required onInput={(e) => setDate(e.currentTarget.value)} />
          </label>
          {allDay ? null : (
            <label class="psheet__field">
              <span>Time</span>
              <input type="time" value={time} required onInput={(e) => setTime(e.currentTarget.value)} />
            </label>
          )}
        </div>
        <label class="psheet__check">
          <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.currentTarget.checked)} />
          <span>All day</span>
        </label>
        <label class="psheet__field">
          <span>Repeat</span>
          <select value={repeat} onChange={(e) => setRepeat(e.currentTarget.value as RepeatChoice)}>
            {REPEAT_OPTIONS.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <fieldset class="psheet__seg">
          <legend>Who sees it</legend>
          <label class={visibility === 'family' ? 'is-on' : ''}>
            <input
              type="radio"
              name="pvis"
              value="family"
              checked={visibility === 'family'}
              onChange={() => setVisibility('family')}
            />
            {hasFamily ? 'Family' : 'Everyone here'}
          </label>
          <label class={visibility === 'private' ? 'is-on' : ''}>
            <input
              type="radio"
              name="pvis"
              value="private"
              checked={visibility === 'private'}
              onChange={() => setVisibility('private')}
            />
            Only me
          </label>
        </fieldset>
        <label class="psheet__field">
          <span>Remind me</span>
          <select value={remind} onChange={(e) => setRemind(e.currentTarget.value as RemindChoice)}>
            {REMIND_OPTIONS.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        {note ? (
          <div class="psheet__note" role="alert">
            {note}
          </div>
        ) : null}
        <button type="submit" class="personal__primary" disabled={busy}>
          {editing ? 'Save changes' : 'Add to the calendar'}
        </button>
        {editing && !confirmCancel ? (
          <button type="button" class="psheet__quiet" disabled={busy} onClick={() => setConfirmCancel(true)}>
            Cancel this event
          </button>
        ) : null}
        {editing && confirmCancel ? (
          <div class="psheet__confirm">
            <span>Cancel “{editing.event.title ?? 'this event'}”? It stays on the calendar, struck, with the word.</span>
            <div class="psheet__confirm-row">
              <button type="button" class="psheet__danger" disabled={busy} onClick={cancelEvent}>
                Cancel event
              </button>
              <button type="button" class="personal__secondary" disabled={busy} onClick={() => setConfirmCancel(false)}>
                Keep it
              </button>
            </div>
          </div>
        ) : null}
      </form>
    </PersonalSheet>
  );
}

function ReminderSheet({
  writeTenant,
  onClose,
  onDone,
}: {
  writeTenant: string;
  onClose: () => void;
  onDone: (receipt: string) => void;
}) {
  const [text, setText] = useState('');
  const [date, setDate] = useState(localDate(new Date()));
  const [time, setTime] = useState(nextHour());
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  async function save(e: Event) {
    e.preventDefault();
    if (busy) return;
    const what = text.trim();
    if (!what) {
      setNote('A reminder needs words.');
      return;
    }
    setBusy(true);
    setNote(null);
    try {
      const r = await createReminder(writeTenant, {
        text: what,
        fireAt: `${date}T${time}`,
        timezone: localZone(),
        visibility: 'private',
      });
      onDone(`Reminder set — “${r.text}”, ${wallClockLabel(r.fireAtLocal)}.`);
    } catch (err) {
      setNote(`Not set — ${failureOf(err)}`);
      setBusy(false);
    }
  }

  return (
    <PersonalSheet title="Remind me" onClose={onClose}>
      <form class="psheet__form" onSubmit={save}>
        <label class="psheet__field">
          <span>What</span>
          <input type="text" value={text} maxLength={1000} onInput={(e) => setText(e.currentTarget.value)} />
        </label>
        <div class="psheet__row">
          <label class="psheet__field">
            <span>Date</span>
            <input type="date" value={date} required onInput={(e) => setDate(e.currentTarget.value)} />
          </label>
          <label class="psheet__field">
            <span>Time</span>
            <input type="time" value={time} required onInput={(e) => setTime(e.currentTarget.value)} />
          </label>
        </div>
        {note ? (
          <div class="psheet__note" role="alert">
            {note}
          </div>
        ) : null}
        <button type="submit" class="personal__primary" disabled={busy}>
          Set reminder
        </button>
      </form>
    </PersonalSheet>
  );
}

/** The reminders still to come, and today's that already went — both
 * tenants', one list. A failed one is said loudly, with Core's own reason. */
function PersonalReminders({
  tenants,
  reloadKey,
  onCancelled,
}: {
  tenants: string[];
  reloadKey: number;
  onCancelled: (text: string) => void;
}) {
  const [rows, setRows] = useState<Array<ReminderApi & { tenantId: string }> | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const tenantsKey = tenants.join(',');

  useEffect(() => {
    let cancelled = false;
    const startOfDay = new Date();
    startOfDay.setHours(0, 0, 0, 0);
    Promise.all(tenants.map((t) => listReminders(t).then((rs) => rs.map((r) => ({ ...r, tenantId: t })))))
      .then((all) => {
        if (cancelled) return;
        const keep = all.flat().filter((r) =>
          r.status === 'pending' || r.status === 'claimed'
            ? true
            : (r.status === 'sent' || r.status === 'failed') && new Date(r.fireAt) >= startOfDay
        );
        keep.sort((a, b) => a.fireAt.localeCompare(b.fireAt));
        setRows(keep);
        setFailure(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) setFailure(failureOf(err));
      });
    return () => {
      cancelled = true;
    };
    // tenantsKey stands in for the array, which is rebuilt every render.
  }, [tenantsKey, reloadKey]);

  async function cancel(r: ReminderApi & { tenantId: string }) {
    setBusy(r.id);
    try {
      await cancelReminder(r.tenantId, r.id);
      onCancelled(r.text);
    } catch (err) {
      setFailure(failureOf(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <section class="pcal__reminders" aria-label="Reminders">
      <h2 class="personal__section">Reminders</h2>
      {failure ? <div class="personal__failure">The reminders could not be read — {failure}</div> : null}
      {rows && rows.length === 0 ? <div class="personal__empty">No reminders waiting.</div> : null}
      {rows && rows.length > 0 ? (
        <ul class="pcal__reminder-list">
          {rows.map((r) => (
            <li key={r.id} class={`pcal__reminder${r.status === 'failed' ? ' is-failed' : ''}`}>
              <span class="pcal__reminder-text">{r.text}</span>
              <span class="pcal__reminder-when">
                {wallClockLabel(r.fireAtLocal)}
                {r.status === 'sent' ? ' · sent' : null}
                {r.status === 'claimed' ? ' · sending' : null}
                {r.status === 'failed' ? ` · failed${r.lastError ? ` — ${r.lastError}` : ''}` : null}
              </span>
              {r.status === 'pending' ? (
                <button
                  type="button"
                  class="pcal__reminder-cancel"
                  disabled={busy === r.id}
                  aria-label={`Cancel the reminder ${r.text}`}
                  onClick={() => cancel(r)}
                >
                  Cancel
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
